import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/admin/StudentForm.scss";
import DropdownSelect from "../common/DropdownSelect";

const StudentForm = ({ isOpen, onClose, onSubmit, initialData, type }) => {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    register_no: "",
    password: "",
    grade: {},
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    else if (formData.name.trim().length < 3)
      newErrors.name = "Name must be at least 3 characters";

    const emailProvided = formData.email.trim().length > 0;
    const regProvided = formData.register_no.trim().length > 0;
    if (!emailProvided && !regProvided) {
      newErrors.email = "Either Email or Register No. is required";
      newErrors.register_no = "Either Email or Register No. is required";
    }
    if (emailProvided) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
        newErrors.email = "Please enter a valid email";
    }

    if (!initialData) {
      if (!formData.password) newErrors.password = "Password is required";
      else if (formData.password.length < 6)
        newErrors.password = "Password must be at least 6 characters";
    }
    if (
      !formData?.grade ||
      (typeof formData.grade === "object" &&
        Object.keys(formData.grade).length === 0) ||
      (typeof formData.grade === "string" && !formData.grade.trim())
    ) {
      newErrors.grade = "Please select the grade";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      email: formData?.email,
      name: formData?.name,
      password: formData?.password,
      register_no: formData?.register_no,
      grade:
        formData?.grade?.value ||
        (typeof formData?.grade === "string" ? formData.grade : undefined),
    };

    if (initialData && !payload.password) delete payload.password;
    onSubmit(payload);
  };

  const handleClose = () => {
    onClose();
    setErrors({});
    setFormData({});
  };

  useEffect(() => {
    if (initialData) {
      const { name, email, register_no, isDisabled, grade } = initialData;
      setFormData({
        name: name || "",
        email: email || "",
        register_no: register_no || "",
        password: "",
        grade: grade ? { value: grade, label: grade } : {},
      });
    } else {
      setFormData({
        name: "",
        email: "",
        register_no: "",
        password: "",
        grade: {},
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="student-form-overlay">
      <div className="student-form-container">
        <div className="student-form-header">
          <h2>{initialData ? "Edit" : "Create"} Student</h2>
          <button
            className="student-close-button"
            onClick={handleClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="student-form">
          <div
            className={`student-form-group ${errors.name ? "has-error" : ""}`}
          >
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter full name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "name-error" : undefined}
            />
            {errors.name && (
              <span id="name-error" className="student-error-message">
                {errors.name}
              </span>
            )}
          </div>

          <div
            className={`student-form-group ${errors.email ? "has-error" : ""}`}
          >
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
              autoComplete="email"
            />
            {errors.email && (
              <span id="email-error" className="student-error-message">
                {errors.email}
              </span>
            )}
          </div>

          <div
            className={`student-form-group ${
              errors.register_no ? "has-error" : ""
            }`}
          >
            <label htmlFor="register_no">Register No.</label>
            <input
              id="register_no"
              name="register_no"
              type="text"
              value={formData.register_no}
              onChange={handleChange}
              placeholder="Enter register number"
              aria-invalid={!!errors.register_no}
              autoComplete="off"
            />
            {errors.register_no && (
              <span id="register-no-error" className="student-error-message">
                {errors.register_no}
              </span>
            )}
          </div>
          <div
            className={`student-form-dropdown-container ${
              errors.grade ? "has-error" : ""
            }`}
          >
            <label>Grade </label>
            <DropdownSelect
              options={
                type === "school"
                  ? [
                      { value: "6", label: "6" },
                      { value: "7", label: "7" },
                      { value: "8", label: "8" },
                      { value: "9", label: "9" },
                      { value: "10", label: "10" },
                      { value: "11", label: "11" },
                      { value: "12", label: "12" },
                    ]
                  : [
                      { value: "1 year", label: "1 year" },
                      { value: "2 year", label: "2 year" },
                      { value: "3 year", label: "3 year" },
                      { value: "4 year", label: "4 year" },
                    ]
              }
              value={formData?.grade}
              onChange={(vals) => {
                setFormData((prev) => ({
                  ...prev,
                  grade: vals,
                }));
              }}
              placeholder="Select grade"
              searchable
            />
            {errors.grade && (
              <span id="grade" className="student-error-message">
                {errors.grade}
              </span>
            )}
          </div>

          {!initialData && (
            <div
              className={`student-form-group ${
                errors.password ? "has-error" : ""
              }`}
            >
              <label htmlFor="password">Password</label>
              <div className="password-input">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 5C6.5 5 2.1 8.6 1 12c1.1 3.4 5.5 7 11 7s9.9-3.6 11-7c-1.1-3.4-5.5-7-11-7z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 3l18 18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M12 5C6.5 5 2.1 8.6 1 12c.49 1.5 1.67 3.1 3.36 4.41M20.64 16.41C22.33 15.1 23.51 13.5 24 12c-1.1-3.4-5.5-7-12-7"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      <path
                        d="M9.5 9.5a3.5 3.5 0 004.95 4.95"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span id="password-error" className="student-error-message">
                  {errors.password}
                </span>
              )}
            </div>
          )}

          <div className="student-form-actions">
            <button
              type="button"
              className="student-cancel-button"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="student-submit-button btn-interactive"
            >
              {initialData ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentForm;
